import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import DashboardService from "services/DashboardService";

export const getNumberOfSchools = createAsyncThunk(
    "dashboard/getNumberOfSchools",
    async (data, { rejectWithValue }) => {
        try {
            const response = await DashboardService.getNumberOfSchools();
            return response.data?.number;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getTrainingsTurnover = createAsyncThunk(
    "dashboard/getTrainingsTurnover",
    async (data, { rejectWithValue }) => {
        try {
            const response = await DashboardService.getTrainingsTurnover();
            return {
                monthly:
                    response.data?.globalTurnoverByMonth?.globalTurnoverByMonth,
                annual: response.data?.messages?.globalTurnoverByYear,
            };
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getBestTrainings = createAsyncThunk(
    "dashboard/getBestTrainings",
    async (data, { rejectWithValue }) => {
        try {
            const response = await DashboardService.getBestTrainings();
            return response.data?.formations.sort(
                (a, b) => a.ranking - b.ranking
            );
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getNumberOfStudentsBySchool = createAsyncThunk(
    "dashboard/getNumberOfStudentsBySchool",
    async (schoolId, { rejectWithValue }) => {
        try {
            const response = await DashboardService.getNumberOfStudentsBySchool(
                schoolId
            );
            return response.data?.number;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getNumberOfInstructorsBySchool = createAsyncThunk(
    "dashboard/getNumberOfInstructorsBySchool",
    async (schoolId, { rejectWithValue }) => {
        try {
            const response =
                await DashboardService.getNumberOfInstructorsBySchool(schoolId);
            return response.data?.number;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getTrainingsTurnoverBySchool = createAsyncThunk(
    "dashboard/getTrainingsTurnoverBySchool",
    async (schoolId, { rejectWithValue }) => {
        try {
            const response =
                await DashboardService.getTrainingsTurnoverBySchool(schoolId);
            return {
                monthly:
                    response?.data?.globalTurnoverByMonth
                        ?.globalTurnoverByMonth,
                annual: response.data?.messages?.globalTurnoverByYear,
            };
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getSpendingRateBySchool = createAsyncThunk(
    "dashboard/getSpendingRateBySchool",
    async (schoolId, { rejectWithValue }) => {
        try {
            const response = await DashboardService.getSpendingRateBySchool(
                schoolId
            );
            return response.data?.totalAmount;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getSchoolsStats = createAsyncThunk(
    "dashboard/getSchoolsStats",
    async (data, { rejectWithValue }) => {
        try {
            const response = await DashboardService.getSchoolsStats();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getSchoolSpendingsAndRevenues = createAsyncThunk(
    "dashboard/getSchoolSpendingsAndRevenues",
    async (schoolId, { rejectWithValue }) => {
        try {
            const response =
                await DashboardService.getSpendingsAndRevenuesBySchool(
                    schoolId
                );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getTheProfitRate = createAsyncThunk(
    "dashboard/getTheProfitRate",
    async ({ startDate, endDate }, { rejectWithValue }) => {
        try {
            const response = await DashboardService.getTheProfitRate({
                startDate,
                endDate,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

const dashboardSlice = createSlice({
    name: "dashboard",
    initialState: {
        loading: false,
        error: null,
        numberOfSchools: 0,
        trainingsTurnover: {
            monthly: 0,
            annual: 0,
        },
        bestTrainings: [],
        profitRate: {
            monthly: 0,
            annual: 0,
        },
        school: {
            schoolId: null,
            schoolName: "",
            numberOfStudents: 0,
            numberOfInstructors: 0,
            trainingsTurnover: {
                monthly: 0,
                annual: 0,
            },
            spendingRate: 0,
        },
        schoolsStats: [],
        schoolSpendingsAndRevenues: [],
    },
    reducers: {
        showLoading: (state) => {
            state.loading = true;
        },
        hideLoading: (state, action) => {
            state.loading = false;
        },
        hideError: (state, action) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getNumberOfSchools.pending, (state) => {
                state.loading = true;
            })
            .addCase(getNumberOfSchools.fulfilled, (state, action) => {
                state.numberOfSchools = action.payload;
                state.loading = false;
            })
            .addCase(getNumberOfSchools.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getTrainingsTurnover.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTrainingsTurnover.fulfilled, (state, action) => {
                state.trainingsTurnover = action.payload;
                state.loading = false;
            })
            .addCase(getTrainingsTurnover.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getBestTrainings.pending, (state) => {
                state.loading = true;
            })
            .addCase(getBestTrainings.fulfilled, (state, action) => {
                state.bestTrainings = action.payload;
                state.loading = false;
            })
            .addCase(getBestTrainings.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getTheProfitRate.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTheProfitRate.fulfilled, (state, action) => {
                state.profitRate = action.payload;
                state.loading = false;
            })
            .addCase(getTheProfitRate.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getNumberOfStudentsBySchool.pending, (state) => {
                state.loading = true;
            })
            .addCase(getNumberOfStudentsBySchool.fulfilled, (state, action) => {
                state.school.numberOfStudents = action.payload;
                state.loading = false;
            })
            .addCase(getNumberOfStudentsBySchool.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getNumberOfInstructorsBySchool.pending, (state) => {
                state.loading = true;
            })
            .addCase(
                getNumberOfInstructorsBySchool.fulfilled,
                (state, action) => {
                    state.school.numberOfInstructors = action.payload;
                    state.loading = false;
                }
            )
            .addCase(
                getNumberOfInstructorsBySchool.rejected,
                (state, action) => {
                    state.error = action.payload;
                    state.loading = false;
                }
            )
            .addCase(getTrainingsTurnoverBySchool.pending, (state) => {
                state.loading = true;
            })
            .addCase(
                getTrainingsTurnoverBySchool.fulfilled,
                (state, action) => {
                    state.school.trainingsTurnover = action.payload;
                    state.loading = false;
                }
            )
            .addCase(getTrainingsTurnoverBySchool.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getSpendingRateBySchool.pending, (state) => {
                state.loading = true;
            })
            .addCase(getSpendingRateBySchool.fulfilled, (state, action) => {
                state.school.spendingRate = action.payload;
                state.loading = false;
            })
            .addCase(getSpendingRateBySchool.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getSchoolsStats.pending, (state) => {
                state.loading = true;
            })
            .addCase(getSchoolsStats.fulfilled, (state, action) => {
                state.schoolsStats = action.payload;
                state.loading = false;
            })
            .addCase(getSchoolsStats.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getSchoolSpendingsAndRevenues.pending, (state) => {
                state.loading = true;
            })
            .addCase(
                getSchoolSpendingsAndRevenues.fulfilled,
                (state, action) => {
                    state.schoolSpendingsAndRevenues = action.payload;
                    state.loading = false;
                }
            )
            .addCase(
                getSchoolSpendingsAndRevenues.rejected,
                (state, action) => {
                    state.error = action.payload;
                    state.loading = false;
                }
            );
    },
});

export default dashboardSlice.reducer;
export const { showLoading, hideLoading, hideError } = dashboardSlice.actions;
